import BaseBoosterBehavior, { IBaseBoosterBehaviorPlayMeta } from './BaseBoosterBehavior';
import CardStateHelper from '../CardStateHelper';
import { CardTask } from '../CardTasks';
import { BoosterType } from '../BoosterType';
import Game from 'Game';
import { FBTrackingEvent } from 'Utility/Tracking/FBTrackingEvent';
import { track, TrackMethod } from 'Utility/Tracking/Track';
import { TransactionType } from 'Utility/Tracking/DataSet/Transaction';
import { IGameState } from '../../Game/IGameState';
import { Quests } from '../../Player/QuestType';
import { InventoryStateHelper } from 'State/Player/Inventory/InventoryStateHelper';
import { QuestStateHelper } from 'State/Player/Quests/QuestStateHelper';
import { Random } from 'Utility/Random';

export default class PlusFiveBoosterBehavior extends BaseBoosterBehavior {
    public static readonly cardsToAdd = 5;

    public play(state: IGameState, meta?: IBaseBoosterBehaviorPlayMeta): IGameState {
        let cardsState = {
            ...state.levelState.cardsState,
        };

        const targets: string[] = [];

        // Draw 5 random cards.
        const cardValues: number[] = CardStateHelper.drawNewCardsValues(PlusFiveBoosterBehavior.cardsToAdd);

        const isFirstUse = state.playerState.plusFiveBoostersUsed === 0;
        const isSecondUse = state.playerState.plusFiveBoostersUsed === 1;

        // On first or second use we guarantee the player receives at least one matching card in a particular position.
        if (isFirstUse || isSecondUse) {
            // Draw a match.
            const match = CardStateHelper.drawInPlayMatch(cardsState);

            if (match !== null) {
                // Splice match into `cardValues` based on particular conditions.
                if (isFirstUse) {
                    // 50% chance for match to be in first or second position.
                    if (Random.roll(0.5)) {
                        cardValues.splice(0, 1, match);
                    } else {
                        cardValues.splice(1, 1, match);
                    }
                } else if (isSecondUse) {
                    // Match is always in 5th position.
                    cardValues.splice(4, 1, match);
                }
            }
        }

        cardsState = CardStateHelper.addNewCardsInStack(cardsState, cardValues.reverse(), targets);

        // Trigger plus cards animation task.
        cardsState = CardStateHelper.setStateTaskData(cardsState, {
            task: CardTask.PLUS_FIVE,
            cards: targets,
        });

        state = {
            ...state,
            levelState: {
                ...state.levelState,
                cardsState: cardsState,
            },
            playerState: {
                ...state.playerState,

                // Increment plus 5 booster usages.
                plusFiveBoostersUsed: state.playerState.plusFiveBoostersUsed + 1,
            },
        };

        return this.consumeItemOrCoins(state);
    }

    protected getPrice(state: IGameState): number {
        return state.levelState.boosterPrices[BoosterType.PLUS_FIVE];
    }

    public getInventoryItem(state: IGameState): number {
        return state.playerState.inventory[BoosterType.PLUS_FIVE];
    }

    protected spendInventoryItem(state: IGameState): IGameState {
        track('transactionType', TransactionType.INVENTORY);
        // TODO: use InventoryStateHelper.consumeInventory instead
        let playerState = InventoryStateHelper.setBoosterPlusFive(
            state.playerState,
            state.playerState.inventory[BoosterType.PLUS_FIVE] - 1,
            { queueTask: false }
        );
        return {
            ...state,
            playerState,
        };
    }

    protected spendCoins(state: IGameState): IGameState {
        super.trackCoinsSpent(state, 'tExtraCardsCoins');
        track('tPlus5Received', 1, TrackMethod.ADD);
        state = super.spendCoins(state);
        track('tPlus5Used', 1, TrackMethod.ADD);
        track('mExtraCardsCoins', PlusFiveBoosterBehavior.cardsToAdd, TrackMethod.ADD);

        Game.sdk.gameTracking(FBTrackingEvent.TapOnBuyFiveCards);

        return {
            ...state,
            levelState: {
                ...state.levelState,
                boosterUses: {
                    ...state.levelState.boosterUses,
                    [BoosterType.PLUS_FIVE]: state.levelState.boosterUses[BoosterType.PLUS_FIVE] + 1,
                },
            },
        };
    }

    protected consumeItemOrCoins(state: IGameState): IGameState {
        track('mPlus5Used', 1, TrackMethod.ADD);

        let newState = {
            ...state,
            playerState: QuestStateHelper.incrementQuestProgressBy(state.playerState, Quests.USE_PLUS_5_CARDS, 1),
        };

        return super.consumeItemOrCoins(newState);
    }
}
